import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SaVoucherInfo, {
  SaVoucherNotice,
} from '@/components/Approval/@components/SaVoucherInfo';
import useSaVoucherDraftSource from '@/hooks/approval/useSaVoucherDraftSource';
import type { SaVoucherSnapshot } from '@/utils/approval/saVoucher';
import { FieldBlock, FieldHeader } from '../styles';
import type { ApprovalFormValues } from '../types';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';

type SaVoucherFieldProps = {
  /** 진입 쿼리 `Ep` 의 SAP 묶음번호 — 신규 작성 전용 */
  entrySapDocumentNo?: string;
  /** 편집 진입 복원값(단건조회 attributes) */
  initialVoucher?: SaVoucherSnapshot | null;
};

/**
 * SA 전표(99) 「전표 정보」 필드 — 본문 위. 묶음번호는 SAP 진입 URL 로만 정해지고 선택 UI 없음(레거시 동일).
 * 편집은 저장 스냅샷을 먼저 깔고 draft-source 로 최신 라인을 다시 받음 — 저장 때 서버도 SAP 를 재조회
 */
const SaVoucherField = ({
  entrySapDocumentNo,
  initialVoucher,
}: SaVoucherFieldProps) => {
  const { t } = useTranslation();
  const { setValue, getValues, watch } = useFormContext<ApprovalFormValues>();
  const saVoucher = watch('saVoucher');

  useEffect(() => {
    if (!initialVoucher || getValues('saVoucher')) return;
    setValue('saVoucher', initialVoucher, { shouldDirty: false });
  }, [initialVoucher, getValues, setValue]);

  const sapDocumentNo = initialVoucher?.sapDocumentNo ?? entrySapDocumentNo;
  const { data, errorKind, isLoading } = useSaVoucherDraftSource(sapDocumentNo);

  // 같은 화면에서 Ep 만 바뀌면 폼이 유지돼 이전 묶음번호 전표로 상신될 수 있음
  useEffect(() => {
    const current = getValues('saVoucher');
    if (current && current.sapDocumentNo !== sapDocumentNo) {
      setValue('saVoucher', null, { shouldDirty: false });
    }
  }, [sapDocumentNo, getValues, setValue]);

  useEffect(() => {
    if (!data || data.attributes.sapDocumentNo !== sapDocumentNo) return;
    setValue(
      'saVoucher',
      {
        sapDocumentNo: data.attributes.sapDocumentNo,
        zSum: data.attributes.zSum,
        vouchers: data.display.vouchers ?? [],
      },
      { shouldDirty: false },
    );
  }, [data, sapDocumentNo, setValue]);

  const message = useMemo(() => {
    if (!sapDocumentNo) return t('SAP 전표 정보가 없습니다.');
    // 편집은 저장 스냅샷이 있어 조회 실패여도 표를 유지
    if (saVoucher?.vouchers.length) return null;
    if (errorKind === 'not-found') return t('SAP 전표를 찾을 수 없습니다.');
    if (errorKind === 'sap-unavailable')
      return t(
        'SAP 전표 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
      );
    if (isLoading || !saVoucher) return t('전표 정보를 불러오는 중입니다…');
    return t('SAP 전표를 찾을 수 없습니다.');
  }, [sapDocumentNo, saVoucher, errorKind, isLoading, t]);

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t('전표 정보')}</InfoSectionTitle>
      </FieldHeader>
      {message || !saVoucher ? (
        <SaVoucherNotice
          message={message ?? ''}
          isError={Boolean(errorKind) || !sapDocumentNo}
        />
      ) : (
        <SaVoucherInfo vouchers={saVoucher.vouchers} zSum={saVoucher.zSum} />
      )}
    </FieldBlock>
  );
};

export default SaVoucherField;
